import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useSuperAdminBookingsFilter } from '@/features/superAdmin/features/data/bookings/hooks/useSuperAdminBookingsFilter';
import { SuperAdminBookingListResponseDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    suspendFetching?: boolean;
};

export const useSuperAdminBookingsListApi = ({ suspendFetching }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useSuperAdminBookingsFilter();
    const {
        take,
        skip,
        startDate,
        endDate,
        tenantId,
        hotelId,
        ownerId,
        apartmentId,
        documentNumber,
        orderBy,
        orderDirection,
    } = filter;

    const { data, isLoading, error } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.bookings.queryKeys.list,
            take,
            skip,
            startDate,
            endDate,
            tenantId,
            hotelId,
            ownerId,
            apartmentId,
            documentNumber,
            orderBy,
            orderDirection,
        ],
        queryFn: async (): Promise<SuperAdminBookingListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.bookings.endpoints.list,
                {
                    skip,
                    take,
                    startDate: DateTimeHelper.getDateOnly(startDate),
                    endDate: DateTimeHelper.getDateOnly(endDate),
                    tenantId: tenantId ?? undefined,
                    hotelId: hotelId ?? undefined,
                    ownerId: ownerId ?? undefined,
                    apartmentId: apartmentId ?? undefined,
                    documentNumber: documentNumber ?? undefined,
                    orderBy: orderBy ?? undefined,
                    orderDirection,
                },
            );
            const response = await axiosInstance.get<SuperAdminBookingListResponseDto>(url);
            return response.data;
        },
        enabled: !suspendFetching,
        placeholderData: keepPreviousData,
    });

    return {
        list: data,
        isLoading,
        error,
    };
};
